export const locales = ['ru', 'en', 'en-US', 'en-GB'] as const;
export type Locale = (typeof locales)[number];
export type BaseLocale = 'ru' | 'en';
export const defaultLocale: Locale = 'ru';
export const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://dev48.org';

export const localeNames: Record<Locale, string> = {
  ru: 'Русский',
  en: 'English',
  'en-US': 'English (USA)',
  'en-GB': 'English (UK)',
};

export function getLocaleFromPathname(pathname: string): Locale {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  if (locales.includes(firstSegment as Locale)) {
    return firstSegment as Locale;
  }
  return defaultLocale;
}

export function getPathWithoutLocale(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  if (locales.includes(firstSegment as Locale)) {
    return '/' + segments.slice(1).join('/');
  }
  return pathname;
}

export function getPathWithLocale(pathname: string, locale: Locale): string {
  const pathWithoutLocale = getPathWithoutLocale(pathname);
  return `/${locale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}` || `/${locale}`;
}

/**
 * Maps regional locales (en-US, en-GB) to their base locale (en)
 * for use with metadata objects that only define 'ru' and 'en' keys.
 */
export function getBaseLocale(locale: string): BaseLocale {
  if (locale === 'ru') return 'ru';
  return 'en'; // en, en-US, en-GB all map to 'en'
}

export function isEnglishLocale(locale: string): boolean {
  return getBaseLocale(locale) === 'en';
}

export function getOpenGraphLocale(locale: string): string {
  if (locale === 'en-GB') return 'en_GB';
  if (locale === 'en' || locale === 'en-US') return 'en_US';
  return 'ru_RU';
}

export function getAlternateOpenGraphLocales(locale: string): string[] {
  const currentLocale = getOpenGraphLocale(locale);
  return ['ru_RU', 'en_US', 'en_GB'].filter((value) => value !== currentLocale);
}

function normalizeMetadataPath(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '';
  }

  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function getCanonicalUrl(locale: string, pathname: string): string {
  const normalizedPath = normalizeMetadataPath(pathname);
  return `${siteUrl}/${locale}${normalizedPath}`;
}

export function getLanguageAlternates(pathname: string): Record<string, string> {
  const normalizedPath = normalizeMetadataPath(pathname);

  // hreflang targets must return 200: /en-US and /en-GB are 301 redirects to /en,
  // so they must not be advertised as alternates.
  return {
    'x-default': `${siteUrl}/${defaultLocale}${normalizedPath}`,
    ru: `${siteUrl}/ru${normalizedPath}`,
    en: `${siteUrl}/en${normalizedPath}`,
  };
}
